<template>
  <div ref="containerRef" class="relative flex flex-col">
    <!-- Trigger = search input field (is both the click target and the query) -->
    <div
      :class="[
        'flex flex-1 w-full items-center gap-2.5 py-3 px-4 sm:min-h-[4rem] rounded-t-full sm:rounded-l-full sm:rounded-r-none transition-colors cursor-text hover:bg-muted/40',
      ]"
      @click="onTriggerClick"
    >
      <Icon name="tabler:map-pin" class="shrink-0 text-foreground" size="22" />
      <input
        ref="inputRef"
        v-model="inputValue"
        type="text"
        :placeholder="selectedName || 'Where to?'"
        autocomplete="off"
        class="min-w-0 flex-1 cursor-pointer bg-transparent text-base font-semibold text-foreground placeholder:text-foreground focus:cursor-text focus:outline-none"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="onKeydown"
      />
      <button
        v-if="selectedName && !isOpen"
        type="button"
        class="shrink-0 rounded-full p-0.5 text-muted-foreground transition-colors hover:text-foreground"
        @mousedown.prevent="clearSelection"
      >
        <Icon name="tabler:x" size="14" />
      </button>
      <Icon
        name="tabler:chevron-down"
        size="14"
        :class="['shrink-0 text-muted-foreground/60 transition-transform duration-200', isOpen && 'rotate-180']"
      />
    </div>

    <!-- Panel — teleported to body so it escapes overflow-hidden ancestors -->
    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="panelRef"
        :style="panelStyle"
        class="fixed z-[200] overflow-hidden rounded-2xl border border-border bg-white shadow-2xl"
        @mousedown.stop
      >
        <div class="max-h-[340px] overflow-y-auto py-2">

          <!-- Popular cities (when query is empty or too short) -->
          <template v-if="!inputValue || inputValue.trim().length < 2">
            <!-- Loading -->
            <div v-if="popularLoading" class="flex items-center gap-2.5 px-5 py-5 text-sm text-muted-foreground">
              <Icon name="tabler:loader-2" size="16" class="animate-spin" />
              Loading cities…
            </div>

            <!-- City grid -->
            <div v-else-if="popularCities.length" class="px-3 pb-1">
              <p class="mb-2 px-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Popular cities
              </p>
              <div class="grid grid-cols-2 gap-0.5 sm:grid-cols-3">
                <button
                  v-for="city in popularCities"
                  :key="city.id"
                  type="button"
                  class="city-card flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-muted/60 active:scale-[0.97]"
                  @mousedown.prevent="select(city)"
                >
                  <!-- URL-based icon (custom asset) -->
                  <img
                    v-if="city.icon && isUrlIcon(city.icon)"
                    :src="city.icon"
                    :alt="city.name"
                    class="h-[22px] w-[22px] shrink-0 rounded-sm object-contain"
                  />
                  <!-- Iconify icon (circle-flags, tabler, etc.) -->
                  <Icon
                    v-else
                    :name="city.icon ?? 'tabler:map-pin'"
                    size="22"
                    class="shrink-0"
                  />
                   <span class="truncate text-sm font-semibold text-foreground">{{ city.name }}</span>
                </button>
              </div>
            </div>

            <!-- Fallback hint -->
            <div v-else-if="!popularLoading" class="px-5 py-5 text-sm text-muted-foreground">
              Type a city name to search…
            </div>
          </template>

          <!-- Search results (when query >= 2 chars) -->
          <template v-else>
            <ul role="listbox">
              <li v-if="isLoading" class="flex items-center gap-2.5 px-5 py-4 text-sm text-muted-foreground">
                <Icon name="tabler:loader-2" size="16" class="animate-spin" />
                Searching…
              </li>

              <template v-else-if="results.length">
                <li
                  v-for="(result, index) in results"
                  :key="result.id"
                  role="option"
                  :aria-selected="index === activeIndex"
                  :class="[
                    'city-item flex cursor-pointer items-center gap-3 px-5 py-3 text-sm transition-colors',
                    index === activeIndex
                      ? 'bg-accent text-accent-foreground'
                      : 'text-foreground hover:bg-accent hover:text-accent-foreground',
                  ]"
                  @mousedown.prevent="select(result)"
                  @mousemove="activeIndex = index"
                >
                  <Icon name="tabler:map-pin" size="16" class="shrink-0 text-muted-foreground" />
                  <span class="flex min-w-0 flex-col">
                    <span class="truncate font-semibold">{{ result.name }}</span>
                    <span v-if="result.country" class="truncate text-xs text-muted-foreground">{{ result.country }}</span>
                  </span>
                </li>
              </template>

              <li v-else class="px-5 py-4 text-sm text-muted-foreground">
                No cities found for "{{ inputValue }}"
              </li>
            </ul>
          </template>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onBeforeUnmount, onMounted } from 'vue';
import { gsap } from 'gsap';
import type { GeocodeResult } from '#types/geo';

const emit = defineEmits<{
  select: [result: GeocodeResult];
}>();

const modelValue = defineModel<string>({ default: '' });

// ── Template refs ─────────────────────────────────────────────────────────────
const containerRef = ref<HTMLElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);

// ── State ─────────────────────────────────────────────────────────────────────
const isOpen = ref(false);
const selectedName = ref('');
/** Live value of the text input — doubles as the search query when panel is open. */
const inputValue = ref('');
const results = ref<GeocodeResult[]>([]);
const isLoading = ref(false);
const activeIndex = ref(-1);
const panelStyle = ref<Record<string, string>>({});

// ── Popular cities (IP-based, cached per mount) ───────────────────────────────
const popularCities = ref<GeocodeResult[]>([]);
const popularLoading = ref(false);
let popularFetched = false;

// ── GSAP context ──────────────────────────────────────────────────────────────
let gsapCtx: gsap.Context | null = null;

// ── Panel positioning — aligned to the ancestor <form> ───────────────────────
function getFormRect(): DOMRect | null {
  let el: HTMLElement | null = containerRef.value;
  while (el && el.tagName !== 'FORM') el = el.parentElement;
  return el?.getBoundingClientRect() ?? null;
}

function updatePanelPosition() {
  const rect = getFormRect() ?? containerRef.value?.getBoundingClientRect();
  if (!rect) return;
  panelStyle.value = {
    top: `${rect.bottom + 10}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
  };
}

// ── Open / close ──────────────────────────────────────────────────────────────
async function openPanel() {
  if (isOpen.value) return;
  updatePanelPosition();
  isOpen.value = true;
  await nextTick();

  gsapCtx?.revert();
  if (panelRef.value) {
    gsapCtx = gsap.context(() => {
      gsap.from(panelRef.value!, {
        opacity: 0,
        y: -10,
        duration: 0.22,
        ease: 'power3.out',
      });
    });
  }

  loadPopularCities();
}

function closePanel() {
  if (!isOpen.value) return;
  isOpen.value = false;
  gsapCtx?.revert();
  gsapCtx = null;
  inputValue.value = selectedName.value; // restore display
  results.value = [];
  activeIndex.value = -1;
  cancelDebounce();
  abortController?.abort();
}

// ── Input event handlers ─────────────────────────────────────────────────────
function onTriggerClick() {
  if (!isOpen.value) {
    inputValue.value = '';
    inputRef.value?.focus();
  }
}

function onFocus() {
  inputValue.value = '';
  openPanel();
}

function onBlur() {
  // Panel items use @mousedown.prevent so they don't steal focus from the input.
  // This fires only when the user genuinely moves focus elsewhere (Tab / outside click).
  // Give the event loop a turn so any pending mousedown.prevent can register first.
  setTimeout(() => {
    const active = document.activeElement;
    if (containerRef.value?.contains(active)) return;
    if (panelRef.value?.contains(active)) return;
    closePanel();
  }, 50);
}

function clearSelection() {
  selectedName.value = '';
  inputValue.value = '';
  modelValue.value = '';
}

// ── Load popular cities via IP detection ────────────────────────────────────
async function loadPopularCities() {
  if (popularFetched) {
    if (popularCities.value.length && (!inputValue.value || inputValue.value.trim().length < 2)) {
      await nextTick();
      animateCityCards();
    }
    return;
  }

  popularFetched = true;
  popularLoading.value = true;

  try {
    const geo = await $fetch<{ country?: string | null }>('/api/geo');
    const country = geo.country;
    if (country) {
      const cities = await $fetch<GeocodeResult[]>('/api/cities-popular', {
        query: { country },
      });
      popularCities.value = cities;
    }
  } catch {
    // Silently fall through — user can still type to search
  } finally {
    popularLoading.value = false;
    await nextTick();
    animateCityCards();
  }
}

function animateCityCards() {
  if (!panelRef.value) return;
  const cards = panelRef.value.querySelectorAll('.city-card');
  if (!cards.length) return;
  gsap.from(cards, {
    opacity: 0,
    y: 10,
    stagger: 0.04,
    duration: 0.22,
    ease: 'power2.out',
    clearProps: 'all',
  });
}

// ── Close on outside click ────────────────────────────────────────────────────
function onDocumentClick(e: MouseEvent) {
  if (!isOpen.value) return;
  const target = e.target as Node;
  if (containerRef.value?.contains(target)) return;
  if (panelRef.value?.contains(target)) return;
  closePanel();
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick, true);
  window.addEventListener('scroll', updatePanelPosition, { passive: true });
  window.addEventListener('resize', updatePanelPosition, { passive: true });
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick, true);
  window.removeEventListener('scroll', updatePanelPosition);
  window.removeEventListener('resize', updatePanelPosition);
  cancelDebounce();
  abortController?.abort();
  gsapCtx?.revert();
});

// ── Debounce ──────────────────────────────────────────────────────────────────
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
const DEBOUNCE_MS = 300;

function cancelDebounce() {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
}

// ── Fetch search results ──────────────────────────────────────────────────────
let abortController: AbortController | null = null;

async function fetchSuggestions(q: string) {
  abortController?.abort();
  abortController = new AbortController();
  isLoading.value = true;
  activeIndex.value = -1;
  try {
    const data = await $fetch<GeocodeResult[]>('/api/geocode', {
      query: { q: q.trim(), limit: 6 },
      signal: abortController.signal,
    });
    results.value = data;
    await nextTick();
    if (panelRef.value) {
      gsap.from(panelRef.value.querySelectorAll('.city-item'), {
        opacity: 0,
        x: -8,
        stagger: 0.05,
        duration: 0.2,
        ease: 'power2.out',
        clearProps: 'all',
      });
    }
  } catch {
    results.value = [];
  } finally {
    isLoading.value = false;
  }
}

watch(inputValue, (val) => {
  if (!isOpen.value) return;
  cancelDebounce();
  if (val.trim().length < 2) {
    results.value = [];
    isLoading.value = false;
    return;
  }
  debounceTimer = setTimeout(() => fetchSuggestions(val), DEBOUNCE_MS);
});

// ── Keyboard navigation ───────────────────────────────────────────────────────
function onKeydown(e: KeyboardEvent) {
  const items = inputValue.value.trim().length >= 2 ? results.value : [];

  if (e.key === 'ArrowDown') {
    e.preventDefault();
    activeIndex.value = Math.min(activeIndex.value + 1, items.length - 1);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    activeIndex.value = Math.max(activeIndex.value - 1, -1);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const result = items[activeIndex.value >= 0 ? activeIndex.value : 0];
    if (result) select(result);
  } else if (e.key === 'Escape') {
    closePanel();
    inputRef.value?.blur();
  } else if (e.key === 'Tab') {
    closePanel();
  }
}

// ── Icon helpers ─────────────────────────────────────────────────────────────
/**
 * URL/path strings (‘/icons/cities/x.svg‘, ‘https://…‘) render as <img>.
 * Iconify names (‘circle-flags:lt‘, ‘tabler:map-pin‘) render via <Icon>.
 */
function isUrlIcon(icon: string): boolean {
  return icon.startsWith('/') || icon.startsWith('http://')  || icon.startsWith('https://');
}

// ── Expose — allows parent to programmatically open/focus the picker ──────────
defineExpose({
  focus: () => {
    inputRef.value?.focus();
  },
});

// ── Select ────────────────────────────────────────────────────────────────────
function select(result: GeocodeResult) {
  selectedName.value = result.name;
  inputValue.value = result.name;
  modelValue.value = result.name;
  isOpen.value = false;
  gsapCtx?.revert();
  gsapCtx = null;
  results.value = [];
  activeIndex.value = -1;
  emit('select', result);
}
</script>
